import { Links, Meta, Outlet, Scripts, ScrollRestoration } from "react-router";

// Inline scripts ported verbatim from the old src/index.html <head>/<body>.
const gtmHead = `(function (w, d, s, l, i) {
  w[l] = w[l] || [];
  w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  var f = d.getElementsByTagName(s)[0],
    j = d.createElement(s),
    dl = l != "dataLayer" ? "&l=" + l : "";
  j.async = true;
  j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
  f.parentNode.insertBefore(j, f);
})(window, document, "script", "dataLayer", "GTM-TRRR87TV");`;

const addressablePixel = `!(function (w, d) {
  w.__adrsbl = { queue: [], run: function () { this.queue.push(arguments); } };
  var s = d.createElement("script");
  s.async = true;
  s.src = "https://tag.adrsbl.io/p.js?tid=94b259ad90aa432cb1ebc360bff4835f";
  var b = d.getElementsByTagName("script")[0];
  b.parentNode.insertBefore(s, b);
})(window, document);`;

const pwaDetect = `var isPWA = navigator?.standalone ||
  window.matchMedia("(display-mode: standalone)").matches || false;
if (isPWA) { localStorage.setItem("_____PWA", document.location.href); }`;

const preloaderScript = `window.addEventListener("DOMContentLoaded", () => {
  const p = document.getElementById("before_load_preloader");
  if (p) { p.style.opacity = "0"; setTimeout(() => (p.style.display = "none"), 300); }
});
window.addEventListener("load", () => {
  const p = document.getElementById("before_load_preloader");
  if (p) { p.style.display = "none"; }
});`;

const utmScript = `var value = new URL(location.href).searchParams.get("utm_prid");
if (value) {
  var date = new Date();
  date.setTime(date.getTime() + 30 * 24 * 60 * 60 * 1000);
  document.cookie = "prop_visitor_id=" + (value || "") + "; expires=" + date.toUTCString() + "; path=/";
}`;

const serviceWorkerScript = `async function unregisterServiceWorkers() {
  if ("serviceWorker" in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
      return true;
    } catch (error) {
      console.error("Failed to unregister service workers:", error);
      return false;
    }
  }
  return false;
}
if ("serviceWorker" in navigator && !window.location.search.includes("recovery")) {
  unregisterServiceWorkers();
}`;

const description =
  "Avalanche's leading Web3 gaming hub. Studio & Publisher empowering and nurturing epic games. The ultimate home for indie game devs building on AVAX.";
const orgImage = "https://static.funtico.com/org_image_funtico.png";

// The document shell. Replaces src/index.html — the React Router <Meta/>/<Links/>
// slots render per-route meta() output and asset links; <Scripts/> injects the
// client bundle (see entry.client.tsx). Static head/body markup is ported 1:1.
export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="UTF-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
        />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://funtico.com/" />
        <meta property="og:image" content={orgImage} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://funtico.com/" />
        <meta property="twitter:image" content={orgImage} />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/favicon/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon/favicon-16x16.png"
        />
        <link
          rel="mask-icon"
          href="/favicon/safari-pinned-tab.svg"
          color="#5bbad5"
        />
        <link rel="shortcut icon" href="/favicon/favicon.ico" />
        <link rel="dns-prefetch" href="https://api.funtico.com/" />
        <link rel="dns-prefetch" href="https://static.funtico.com/" />
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta name="theme-color" content="#ffffff" />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content="black-translucent"
        />
        <meta name="apple-mobile-web-app-title" content="Funtico" />
        <script dangerouslySetInnerHTML={{ __html: gtmHead }} />
        <script dangerouslySetInnerHTML={{ __html: addressablePixel }} />
        <script
          type="module"
          dangerouslySetInnerHTML={{ __html: pwaDetect }}
        />
        <Meta />
        <Links />
      </head>
      <body>
        {/* The inline preloader script (below) fades/hides this node on load,
            before React hydrates the document, so its attributes intentionally
            diverge from what React rendered — suppress the hydration warning. */}
        <div
          id="before_load_preloader"
          className="preloader active"
          style={{ opacity: 1, transition: "opacity 0.3s ease" }}
          suppressHydrationWarning
        >
          <div className="fantico_logo">
            <div className="clip_logo"></div>
          </div>
        </div>
        <noscript
          dangerouslySetInnerHTML={{
            __html:
              "<style>#before_load_preloader{display:none !important;}</style>",
          }}
        />
        <script dangerouslySetInnerHTML={{ __html: preloaderScript }} />
        <noscript
          dangerouslySetInnerHTML={{
            __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-TRRR87TV" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
          }}
        />
        {children}
        <script dangerouslySetInnerHTML={{ __html: utmScript }} />
        <script dangerouslySetInnerHTML={{ __html: serviceWorkerScript }} />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

// Root stays minimal so it can render in Node when React Router prerenders the
// SPA fallback (ssr:false). All app-wide providers — which run browser-only code
// (wagmi, gsap, window access) — live in the client-only `providers` layout
// route below, guarded by its HydrateFallback. See adr/023.
export default function page() {
  return <Outlet />;
}

// SPA mode renders this (not the route tree) into the prerendered fallback HTML,
// so the browser-only provider subtree never executes in Node. The visible shell
// is the static preloader in <Layout> above; the client hydrates the real tree.
// HydrateFallback is only permitted on the root route in SPA mode. See adr/023.
export function HydrateFallback() {
  return null;
}
